import { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import {
  Paper, Typography, Grid, Card, CardContent, Chip, Box,
  Table, TableHead, TableRow, TableCell, TableBody, Divider
} from "@mui/material";
import Chart from "react-apexcharts";

const KpiCard = ({ title, value, sub, color }) => (
  <Card sx={{ borderLeft: `6px solid ${color}`, height: "100%" }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
      <Typography variant="h3" sx={{ mt: 1, color, fontWeight: 700 }}>{value}</Typography>
      {sub && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

const COLORS = {
  vigente:        "#2E7D32",
  pendiente:      "#E65100",
  vencido:        "#C62828",
  desactualizado: "#757575",
};

function StackedBar({ titulo, categorias, series }) {
  if (!categorias.length) return (
    <Typography color="text.secondary" sx={{ py: 2 }}>No data</Typography>
  );
  return (
    <Chart
      type="bar"
      height={Math.max(250, categorias.length * 28 + 80)}
      series={series}
      options={{
        chart: { stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
        xaxis: { categories: categorias, labels: { style: { fontSize: "11px" } } },
        legend: { position: "top" },
        colors: [COLORS.vigente, COLORS.pendiente, COLORS.vencido, COLORS.desactualizado],
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
      }}
    />
  );
}

function CumplimientoPorCurso({ cursos }) {
  if (!cursos.length) return (
    <Typography color="text.secondary" sx={{ py: 2 }}>No mandatory courses defined yet.</Typography>
  );

  const categorias = cursos.map(c => c.nombre_curso);
  const pcts       = cursos.map(c => Number(c.pct) || 0);

  // Color per bar: red <50, orange 50-79, green >=80
  const barColors = pcts.map(p =>
    p >= 80 ? "#2E7D32" : p >= 50 ? "#E65100" : "#C62828"
  );

  return (
    <Chart
      type="bar"
      height={Math.max(280, categorias.length * 32 + 80)}
      series={[{ name: "% Compliance", data: pcts }]}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            distributed: true,
            dataLabels: { position: "center" },
          },
        },
        colors: barColors,
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val}%`,
          style: { fontSize: "12px", fontWeight: 600, colors: ["#fff"] },
        },
        xaxis: {
          categories: categorias,
          max: 100,
          labels: { formatter: (v) => `${v}%`, style: { fontSize: "11px" } },
        },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        legend: { show: false },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const c = cursos[dataPointIndex];
            return `<div style="padding:8px;font-size:13px">
              <b>${c.nombre_curso}</b><br/>
              Current: ${c.vigentes} &nbsp; Pending: ${c.pendientes}<br/>
              Expired: ${c.vencidos} &nbsp; Total: ${c.total}<br/>
              <b>Compliance: ${c.pct}%</b>
            </div>`;
          },
        },
        grid: { xaxis: { lines: { show: true } } },
      }}
    />
  );
}

function TablaDetalle({ rows, columnas }) {
  if (!rows.length) return (
    <Typography color="text.secondary" sx={{ py: 2 }}>No records.</Typography>
  );
  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ background: "#111" }}>
          {columnas.map(c => (
            <TableCell key={c.key} sx={{ color: "white", fontWeight: 600 }}>{c.label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i} hover>
            {columnas.map(c => (
              <TableCell key={c.key}>{r[c.key] ?? "—"}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/global");
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        alert("You do not have permission to view the dashboard.");
      } else {
        console.error("Error loading dashboard:", err.response?.data || err.message);
        alert("Error loading the dashboard. Check the console for more details.");
      }
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (!data) return <Typography sx={{ p: 3 }}>Loading dashboard...</Typography>;

  const g          = data.global          || {};
  const porArea    = data.por_area        || [];
  const porPuesto  = data.por_puesto      || [];
  const porCurso   = data.por_curso       || [];
  const vencidos   = data.vencidos        || [];
  const vencen30   = data.vencen_30d      || [];
  const desact     = data.desactualizados || [];

  const total      = g.total        || 0;
  const vigentes   = g.vigentes     || 0;
  const pendientes = g.pendientes   || 0;
  const vencidosN  = g.vencidos     || 0;
  const desactN    = g.desactualizados || 0;
  const pct        = g.pct_cumplimiento ?? 0;

  const mkSeries = (arr, key) => [
    { name: "Current",   data: arr.map(r => r.vigentes        || 0) },
    { name: "Pending",   data: arr.map(r => r.pendientes      || 0) },
    { name: "Expired",   data: arr.map(r => r.vencidos        || 0) },
    { name: "Outdated",  data: arr.map(r => r.desactualizados || 0) },
  ];

  const colsVencidos = [
    { key: "nombre_completo", label: "Employee" },
    { key: "puesto_texto",    label: "Position" },
    { key: "area_texto",      label: "Area" },
    { key: "nombre_curso",    label: "Course" },
    { key: "fecha_vencimiento", label: "Exp. date" },
    { key: "dias_vencido",    label: "Days overdue" },
  ];
  const colsVencen30 = [
    { key: "nombre_completo",  label: "Employee" },
    { key: "puesto_texto",     label: "Position" },
    { key: "nombre_curso",     label: "Course" },
    { key: "fecha_vencimiento", label: "Due" },
    { key: "dias_restantes",   label: "Days remaining" },
  ];
  const colsDesact = [
    { key: "nombre_completo", label: "Employee" },
    { key: "puesto_texto",    label: "Position" },
    { key: "area_texto",      label: "Area" },
    { key: "nombre_curso",    label: "Course" },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Training Dashboard</Typography>

      {/* ── KPIs ─────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Active employees" value={data.total_empleados ?? "—"} color="#1565C0" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="% Compliance" value={`${pct}%`} sub={`${vigentes} of ${total} courses`} color={pct >= 80 ? COLORS.vigente : COLORS.vencido} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Current" value={vigentes} color={COLORS.vigente} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Pending" value={pendientes} color={COLORS.pendiente} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Expired" value={vencidosN} color={COLORS.vencido} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard title="Next 30 days" value={vencen30.length} color="#6A1B9A" />
        </Grid>
        <grid item xs={6} sm={4} md={2}>
          <KpiCard title="Outdated" value={desactN} color={COLORS.desactualizado} />
        </grid>
      </Grid>

      {/* ── Overall pie ───────────────────────────────────────── */}
      {total > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Overall course distribution</Typography>
          <Chart
            type="donut"
            height={280}
            series={[vigentes, pendientes, vencidosN, desactN]}
            options={{
              labels: ["Current", "Pending", "Expired", "Outdated"],
              colors: [COLORS.vigente, COLORS.pendiente, COLORS.vencido, COLORS.desactualizado],
              legend: { position: "bottom" },
              dataLabels: { enabled: true },
              plotOptions: { pie: { donut: { size: "55%" } } },
            }}
          />
        </Paper>
      )}

      {/* ── Compliance by Course ─────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h6">Compliance by Mandatory Course</Typography>
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            {[["≥ 80%", "#2E7D32"], ["50–79%", "#E65100"], ["< 50%", "#C62828"]].map(([label, color]) => (
              <Chip key={label} label={label} size="small"
                sx={{ bgcolor: color, color: "white", fontWeight: 600, fontSize: "11px" }} />
            ))}
          </Box>
        </Box>
        <CumplimientoPorCurso cursos={porCurso} />
      </Paper>

      {/* ── Bars by Area ──────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Compliance by Area</Typography>
        <StackedBar
          categorias={porArea.map(a => a.area || "No area")}
          series={mkSeries(porArea)}
        />
      </Paper>

      {/* ── Bars by Position ─────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Compliance by Position</Typography>
        <StackedBar
          categorias={porPuesto.map(p => p.puesto || "No position")}
          series={mkSeries(porPuesto)}
        />
      </Paper>

      {/* ── Critical alert ────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
        <Chip label={`Expired: ${vencidosN}`}          sx={{ bgcolor: COLORS.vencido,        color: "white", fontWeight: 600 }} />
        <Chip label={`Outdated: ${desactN}`}           sx={{ bgcolor: COLORS.desactualizado,  color: "white", fontWeight: 600 }} />
        <Chip label={`Next 30d: ${vencen30.length}`}   sx={{ bgcolor: "#6A1B9A", color: "white", fontWeight: 600 }} />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ── Expired table ─────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, color: COLORS.vencido }}>
          Expired courses ({vencidos.length})
        </Typography>
        <TablaDetalle rows={vencidos} columnas={colsVencidos} />
      </Paper>

      {/* ── Upcoming expirations table ────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, color: "#6A1B9A" }}>
          Upcoming expirations — 30 days ({vencen30.length})
        </Typography>
        <TablaDetalle rows={vencen30} columnas={colsVencen30} />
      </Paper>

      {/* ── Outdated table ─────────────────────────────────────── */}
      {desact.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, color: COLORS.desactualizado }}>
            Outdated ({desact.length})
          </Typography>
          <TablaDetalle rows={desact} columnas={colsDesact} />
        </Paper>
      )}
    </Paper>
  );
}
